import React, { useEffect, useState } from "react";
import { DeviceMobile, Share, DotsThreeVertical, X, DownloadSimple } from "@phosphor-icons/react";
import { usePwaInstall } from "@/context/PwaInstallContext";

const DISMISS_KEY = "mfg_portal_install_dismissed_at";
const TTL = 14 * 24 * 60 * 60 * 1000;

const isMobile = () => window.matchMedia("(max-width: 1023px)").matches
  || /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

// Portal installs should open on /portal, not the carrier /dashboard — swap the manifest while inside the portal.
export function usePortalManifest() {
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const prev = link.getAttribute("href");
    link.setAttribute("href", "/manifest-portal.json");
    return () => { link.setAttribute("href", prev || "/manifest.json"); };
  }, []);
}

export const INSTALL_DISMISS_KEY = DISMISS_KEY;
export const installBannerWouldShow = () => {
  if (!isMobile()) return false;
  const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return !(at && Date.now() - at < TTL);
};

export default function PortalInstallBanner() {
  const { isInstalled, canInstall, isIOS, promptInstall } = usePwaInstall();
  const [visible, setVisible] = useState(false);
  const [showHow, setShowHow] = useState(false);
  usePortalManifest();

  useEffect(() => {
    if (!isMobile() || isInstalled) return;
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (at && Date.now() - at < TTL) return;
    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, [isInstalled]);

  if (!visible || isInstalled) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* private mode */ }
    setVisible(false);
    window.dispatchEvent(new Event("mfg:install-banner-hidden"));
  };
  const onAdd = async () => {
    if (canInstall) {
      const outcome = await promptInstall();
      if (outcome === "accepted") { setVisible(false); return; }
    }
    setShowHow(true);
  };

  return (
    <div className="lg:hidden mb-4 rounded-2xl bg-[#0A0A0A] text-white p-4 relative overflow-hidden" data-testid="portal-install-banner">
      <button onClick={dismiss} aria-label="Dismiss" data-testid="portal-install-dismiss"
        className="absolute top-2 right-2 w-9 h-9 !min-h-0 grid place-items-center text-white/50 hover:text-white rounded-md">
        <X size={16} weight="bold" />
      </button>
      <div className="flex items-start gap-3 pr-8">
        <div className="w-10 h-10 rounded-lg bg-[#0A84FF] grid place-items-center shrink-0"><DeviceMobile size={20} weight="bold" /></div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-base leading-tight">Add MFG to your home screen</div>
          <div className="text-xs text-white/60 mt-0.5 leading-relaxed">Dispatch board, loads and driver texts one tap away — opens like a native app and stays signed in.</div>
          {!showHow ? (
            <button onClick={onAdd} data-testid="portal-install-add-btn"
              className="mt-3 inline-flex items-center gap-2 bg-[#0A84FF] hover:bg-[#0058B0] px-4 py-2 rounded-md text-sm font-bold">
              <DownloadSimple size={14} weight="bold" /> Add to home screen
            </button>
          ) : (
            <div className="mt-3 rounded-lg bg-white/10 px-3 py-2 text-xs text-white/85 leading-relaxed" data-testid="portal-install-howto">
              {isIOS
                ? <><Share size={14} weight="bold" className="inline align-text-bottom mr-1" /> Tap <strong>Share</strong> in Safari → <strong>Add to Home Screen</strong></>
                : <><DotsThreeVertical size={14} weight="bold" className="inline align-text-bottom mr-1" /> Tap the browser <strong>⋮ menu</strong> → <strong>Add to Home screen</strong> / <strong>Install app</strong></>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
